import { CSSProperties, FC, ReactNode } from 'react';

import styles from './split-panel.module.scss';

interface SplitPanelProps {
  /** Content for the scrollable left panel */
  left: ReactNode;
  /** Content for the sticky right panel */
  right: ReactNode;
  /** Left panel width. Right panel gets the remainder. Default: '66%' */
  leftWidth?: CSSProperties['width'];
}

/**
 * Two-panel layout with a scrollable left panel and a fixed right panel.
 * Used inside FullPageWizard steps where content on the left needs to scroll
 * while the right panel (typically a map) stays in view.
 *
 * The right panel is `overflow: hidden` and assumes its child sizes itself
 * to the container (e.g. a Map with width/height: 100%). Tall content will
 * clip — wrap it in a scroller upstream if you need otherwise.
 *
 * Requires a height-bounded ancestor (a flex parent with a fixed or
 * `flex: 1; min-height: 0` height); the component fills 100% of its parent.
 *
 * @param {SplitPanelProps} props - Left/right content and optional width
 * @return {JSX.Element} The rendered split panel layout
 */
const SplitPanel: FC<SplitPanelProps> = ({
  left,
  right,
  leftWidth = '66%',
}) => (
  <div className={styles.container}>
    <div className={styles.left} style={{ flex: `0 0 ${leftWidth}` }}>
      {left}
    </div>
    <div className={styles.right}>{right}</div>
  </div>
);

export { SplitPanel };
export type { SplitPanelProps };
